/* eslint-disable max-lines-per-function */
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import './editSkillMap.scss';
import FormDialog from '../../../../../Components/Modal/modal';
import {
  CANCEL_TEXT,
  SKILL_MAP_DASHBOARD,
  UPDATE,
} from '../../../../../Constants/strings';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import { Checkbox, Typography } from '@mui/material';
import { editMappedSkills } from '../../../apiCallUtil';
import { AuthContext } from '../../../../../Contexts/AuthContext';
import { logError } from '../../../../../Utils/logger';
import { ReactComponent as CustomCheckedIcon } from '../../../../../Assets/images/svg/dar_custom_check_icon_1.svg';

const EditSkillMap = ({
  data,
  handleClose,
  updateData,
  handleSnackbar,
  setFilteredList,
  setLevelFilteredList,
  activeLevel,
}) => {
  const { userProfile } = useContext(AuthContext);
  const skillLevels = [1, 2, 3, 4];
  const [selectedSkillLevel, setSelectedSkillLevel] = useState(data.skillLevel);
  const [isCoreSkill, setIsCoreSkill] = useState(data.isCoreSkill === 1);
  const [hasChanges, setHasChanges] = useState(false);

  const handleSkillLevelChange = (event) => {
    setSelectedSkillLevel(event.target.value);
    setHasChanges(true);
  };

  const handleCoreSkillChange = (event) => {
    setIsCoreSkill(event.target.checked);
    setHasChanges(true);
  };

  useEffect(() => {
    setSelectedSkillLevel(data.skillLevel);
    setIsCoreSkill(data.isCoreSkill);
    setHasChanges(false);
  }, [data]);

  const updateSkillMap = async () => {
    try {
      const updatedData = {
        userEmail: data.userEmailId,
        userName: data.userName,
        vertical: data.vertical,
        department: data.department,
        userId: data.userId,
        lastUpdatedBy: userProfile.user_name,
        skillDetails: [
          {
            skillLevel: selectedSkillLevel,
            isCoreSkill: isCoreSkill ? 1 : 0,
          },
        ],
        id: data.skillMapId,
      };
      const result = await editMappedSkills(updatedData);
      if (result.failure) {
        logError('Failed to update skill map');
        handleSnackbar(result.failure, 'warning');
      } else {
        const newData = {
          ...data,
          skillLevel: parseInt(selectedSkillLevel),
          isCoreSkill: isCoreSkill ? 1 : 0,
        };
        updateData({ ...newData });

        if (activeLevel === null) {
          await setLevelFilteredList((prevRows) => {
            const index = prevRows.findIndex((row) => row.skillMapId === newData.skillMapId);
            if (index !== -1) {
              const updatedRows = [...prevRows];
              updatedRows[index] = newData;
              return updatedRows;
            }
            return prevRows;
          });
        } else {
          await setLevelFilteredList((prevRows) => {
            return prevRows.filter((row) => row.skillMapId !== newData.skillMapId);
          });
        }

        await setFilteredList((prevRows) => {
          const index = prevRows.findIndex((row) => row.skillMapId === newData.skillMapId);
          if (index !== -1) {
            const updatedRows = [...prevRows];
            updatedRows[index] = newData;
            return updatedRows;
          }
          return prevRows;
        });

        handleSnackbar(SKILL_MAP_DASHBOARD.EDIT.SUCCESS_MESSAGE, 'success');
      }
    } catch (error) {
      logError('Error while updating skill map', error);
    } finally {
      handleClose();
    }
  };

  return (
    <FormDialog
      open={data !== null}
      title={'Edit skill'}
      PaperProps={{}}
      dialogCss="edit-skill-map-dialog-message"
      dialogPaperCss="edit-skill-map-dialog-paper"
      width={360}
      height={205}
      dialogBodycontent={
        <div className="edit-skill-map-dialog-body">
          <div className="skill-map-edit-content">
            <div className="section-1">
              <div className="div-left">{data.skillName}</div>
              <div className="div-right">
                <FormControl>
                  <FormLabel
                    id="skill-level-group"
                    className="skill-level-group"
                  >
                    {SKILL_MAP_DASHBOARD.EDIT.SELECT_LEVELS}
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="skill-level-group"
                    name="row-radio-buttons-group"
                    value={selectedSkillLevel}
                    onChange={handleSkillLevelChange}
                  >
                    {skillLevels.map((level) => (
                      <FormControlLabel
                        key={level}
                        value={String(level)}
                        control={<Radio className="level-radio-button" />}
                        label={String(level)}
                        labelPlacement="bottom"
                        className="level-form-control-label"
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </div>
            </div>
            <div className="section-2">
              <FormControlLabel
                control={
                  <Checkbox
                    className="core-checkbox"
                    checked={isCoreSkill}
                    onChange={handleCoreSkillChange}
                    checkedIcon={<CustomCheckedIcon />}
                  />
                }
                className="core-form-control-label"
                label={
                  <Typography className="core-skill-typography">
                    {SKILL_MAP_DASHBOARD.EDIT.IS_CORE_SKILL_LABEL}
                  </Typography>
                }
              />
            </div>
          </div>
        </div>
      }
      button1={CANCEL_TEXT}
      cancelButtonId={SKILL_MAP_DASHBOARD.EDIT.CANCEL_BUTTON_ID}
      button2={UPDATE}
      submitButtonId={SKILL_MAP_DASHBOARD.EDIT.UPDATE_BUTTON_ID}
      button2Action={updateSkillMap}
      button1Action={() => handleClose()}
      button2Disabled={!hasChanges}
    />
  );
};

EditSkillMap.propTypes = {
  data: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  updateData: PropTypes.func.isRequired,
  handleSnackbar: PropTypes.func,
  setFilteredList: PropTypes.func.isRequired,
  setLevelFilteredList: PropTypes.func.isRequired,
  activeLevel: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.oneOf([null]),
  ]).isRequired,
};

export default EditSkillMap;
